const get = require('lodash/get');
const isEmpty = require('lodash/isEmpty');

require('core-js/fn/array/from');
require('core-js/fn/array/find');

const searchFacets = {
	_eventHandlersInitialized: false,
	/**
	 * @type {Record<string, string[]>|null}
	 */
	_partTerminologyCategoryFacetValueSnapshot: null,
	/**
	 *
	 * @type {Record<string, string[]>|null}
	 */
	_categoryFacetDomReconcileSnapshot: null,
	/**
	 * @type {Record<string, string[]>|null}
	 */
	_lastConfirmedPrimaryCategoryTokens: null,
	constants: {
		ACTIVE_CATEGORY_CLASS: 'active-cat',
	},
};

// Primary facets used as triggers for conditional facet logic
// (must stay in sync with Facet::getPrimaryTriggerFacets()).
const PINNED_FACET_QUERY_NAMES = Object.freeze([
	'autocareCategories',
	'brand',
	'epcCategories',
	'partTerminologyNames',
	'classification_part_terminology_id',
]);

const PRIMARY_FACET_QUERY_NAMES = Object.freeze(
	PINNED_FACET_QUERY_NAMES.filter((name) => name !== 'brand')
);

/** Part-type / terminology facets that narrow category trees (query name varies by stack). */
const PART_TERMINOLOGY_FACET_QUERY_NAMES = Object.freeze([
	'partTerminologyNames',
	'classification_part_terminology_id',
]);

const VEHICLE_FACET_QUERY_NAMES = Object.freeze([
	'year',
	'make',
	'model',
	'trim',
	'engine',
]);

/** Mirrors App\Support\Product\Facet::getCategoryFacetTypes() when DOM metadata is missing. */
const DEFAULT_CATEGORY_FACET_TYPES = Object.freeze(['autocareCategories', 'epcCategories']);

const htmlEntityDecoder = document.createElement('textarea');
const decodeHtmlEntities = (value) => {
	if (typeof value !== 'string') {
		return '';
	}
	htmlEntityDecoder.innerHTML = value;
	return htmlEntityDecoder.value;
};
const normalizeFacetValue = (value) => {
	if (typeof value !== 'string') {
		return '';
	}
	const plusDecoded = value.replaceAll('+', ' ');
	try {
		return decodeHtmlEntities(decodeURIComponent(plusDecoded));
	} catch (e) {
		return decodeHtmlEntities(plusDecoded);
	}
};

/**
 * Escape a facet {@code data-query-name} for use in {@code querySelector} attribute selectors.
 * jsdom does not define {@link CSS}; browsers do.
 *
 * @param {string} queryName
 * @returns {string}
 */
const escapeQueryNameForAttributeSelector = (queryName) => {
	if (typeof queryName !== 'string' || queryName === '') {
		return '';
	}
	if (globalThis.CSS !== undefined && typeof globalThis.CSS.escape === 'function') {
		return globalThis.CSS.escape(queryName);
	}
	const BACKSLASH = '\\';
	return queryName
		.replaceAll(BACKSLASH, String.raw`\\`)
		.replaceAll('"', String.raw`\"`);
};

/**
 * Category checkbox vs facet tokens: exact match, bucket as path under a parent row, or sub-rows when the
 * selection is a collapsed parent only (e.g. {@code ["Engine"]}).
 *
 * @param {Set<string>} wantedNormalized
 * @param {string|null} urlName
 * @param {string|null} name
 * @returns {boolean}
 */
const categoryFacetCheckboxMatchesTokens = (wantedNormalized, urlName, name) => {
	const candidates = [urlName, name]
		.filter((v) => typeof v === 'string' && v !== '')
		.map((v) => normalizeFacetValue(v))
		.filter((c) => c !== '');
	if (candidates.length === 0) {
		return false;
	}
	if (candidates.some((c) => wantedNormalized.has(c))) {
		return true;
	}
	if ([...wantedNormalized].some((w) => candidates.some((c) => typeof w === 'string' && w.startsWith(`${c}>`)))) {
		return true;
	}
	return [...wantedNormalized].some(
		(w) =>
			typeof w === 'string' &&
			w.length > 0 &&
			!w.includes('>') &&
			candidates.some((c) => c.startsWith(`${w}>`))
	);
};

/**
 * @param {string} name
 * @returns {boolean}
 */
const isPartTerminologyFacetIdentifier = (name) =>
	typeof name === 'string' && PART_TERMINOLOGY_FACET_QUERY_NAMES.includes(name);

/**
 * Overlay category tokens from a restore snapshot onto {@code facetValues}. Invoked after the first
 * {@code getFacetValuesFromDom} and again after secondary-facet recompute when
 * {@code isChecked} clears and rebuilds — both passes are required because {@code facetValues} can change between them.
 *
 * @param {Record<string, unknown>} facetValues
 * @param {Record<string, string[]>|null} snapshot
 */
const mergeCategoryRestoreSnapshotIntoFacetValues = (facetValues, snapshot) => {
	if (snapshot === null) {
		return;
	}
	Object.keys(snapshot).forEach((qn) => {
		const arr = snapshot[qn];
		if (Array.isArray(arr) && arr.length > 0) {
			facetValues[qn] = [...arr];
		}
	});
};

searchFacets.initialize = function() {
	try {
		sessionStorage.removeItem('facetExpandedStates');
	} catch (e) {
		// Silently handle storage errors
	}
	searchFacets._partTerminologyCategoryFacetValueSnapshot = null;
	searchFacets._categoryFacetDomReconcileSnapshot = null;
	searchFacets._lastConfirmedPrimaryCategoryTokens = null;
};

searchFacets.initialize();

searchFacets.clearAllFilters = function () {
	searchFacets._partTerminologyCategoryFacetValueSnapshot = null;
	searchFacets._categoryFacetDomReconcileSnapshot = null;
	searchFacets._lastConfirmedPrimaryCategoryTokens = null;
	searchFacets.clearSelectedCategory();
	searchFacets.clearFacetCheckboxes(true);
	searchFacets.handleFacetChange(true);
};

searchFacets.getFacetsModule = function () {
	return rp$('.search-facets-module');
};

/**
 * Copy a non-empty JSON value from any sibling module, or apply {@link DEFAULT_CATEGORY_FACET_TYPES}.
 */
searchFacets.syncCategoryFacetTypesAcrossModules = function () {
	const modules = document.querySelectorAll('.search-facets-module');
	if (modules.length === 0) {
		return;
	}
	let canonical = '';
	for (let i = 0; i < modules.length; i++) {
		const raw = modules[i].getAttribute('data-category-facet-types');
		if (typeof raw !== 'string' || raw.trim() === '') {
			continue;
		}
		const trimmed = raw.trim();
		try {
			const parsed = JSON.parse(trimmed);
			if (Array.isArray(parsed) && parsed.length > 0) {
				canonical = trimmed;
				break;
			}
		} catch (e) {
			canonical = trimmed;
			break;
		}
	}
	if (canonical === '') {
		canonical = JSON.stringify([...DEFAULT_CATEGORY_FACET_TYPES]);
	}
	for (let i = 0; i < modules.length; i++) {
		const el = modules[i];
		const existing = el.getAttribute('data-category-facet-types');
		if (typeof existing !== 'string' || existing.trim() === '') {
			el.setAttribute('data-category-facet-types', canonical);
		}
	}
};

/**
 * Merges unique facet names from every facets module; falls back to defaults when nothing valid is found.
 *
 * @returns {string[]}
 */
searchFacets.getCategoryFacetTypesFromDom = function () {
	searchFacets.syncCategoryFacetTypesAcrossModules();
	const modules = document.querySelectorAll('.search-facets-module');
	/** @type {string[]} */
	const merged = [];
	const seen = new Set();
	for (let i = 0; i < modules.length; i++) {
		const raw = modules[i].getAttribute('data-category-facet-types');
		if (typeof raw !== 'string' || raw.trim() === '') {
			continue;
		}
		try {
			const parsed = JSON.parse(raw.trim());
			if (!Array.isArray(parsed)) {
				continue;
			}
			for (let j = 0; j < parsed.length; j++) {
				const name = parsed[j];
				if (typeof name === 'string' && name !== '' && !seen.has(name)) {
					seen.add(name);
					merged.push(name);
				}
			}
		} catch {
			// ignore malformed JSON
		}
	}
	if (merged.length > 0) {
		return merged;
	}
	return [...DEFAULT_CATEGORY_FACET_TYPES];
};

searchFacets.getFacetCategories = function () {
	return Array.from(document.querySelectorAll('.search-facets-module .category-facet'));
};

searchFacets.clearSelectedCategory = function () {
	const activeClass = searchFacets.constants.ACTIVE_CATEGORY_CLASS;
	document.querySelectorAll(`.search-facets-module .${activeClass}`).forEach((el) => {
		el.classList.remove(activeClass);
	});
};

/**
 * @param {Event} event
 */
searchFacets.handleCategoryFacetClick = function (event) {
	if (!event) {
		return;
	}
	const target = event.currentTarget;
	if (!target) {
		return;
	}
	if (target.classList.contains('mobile')) {
		return;
	}
	if (typeof event.preventDefault === 'function') {
		event.preventDefault();
	}
	const activeClass = searchFacets.constants.ACTIVE_CATEGORY_CLASS;
	if (target.classList.contains(activeClass)) {
		return;
	}
	searchFacets.clearSelectedCategory();
	target.classList.add(activeClass);
	const categoryKey = target.dataset.category || target.id || '';
	searchFacets.handleFacetChange(false, categoryKey, '', true);
};

/**
 * @param {string[]} tokens
 * @param {string} token
 * @param {Set<string>} seenNormalized
 * @returns {void}
 */
const pushUniqueCategoryFacetToken = (tokens, token, seenNormalized) => {
	if (typeof token !== 'string' || token === '') {
		return;
	}
	const normalized = normalizeFacetValue(token);
	if (normalized === '') {
		return;
	}
	if (seenNormalized.has(normalized)) {
		return;
	}
	seenNormalized.add(normalized);
	tokens.push(token);
};

/**
 * @param {Element} input
 * @returns {string}
 */
const getCategoryFacetTokenFromInput = (input) => {
	const urlName = input.dataset.urlName;
	if (typeof urlName === 'string' && urlName !== '') {
		return urlName;
	}
	const name = input.getAttribute('name');
	return typeof name === 'string' ? name : '';
};

/**
 * @param {string[]} tokens
 * @returns {Set<string>}
 */
const buildSeenNormalizedSetForCategoryTokens = (tokens) =>
	new Set(tokens.map((existing) => normalizeFacetValue(existing)).filter((n) => n !== ''));

/**
 * @param {Record<string, string[]>} byQuery
 * @param {string} queryName
 * @returns {{ tokens: string[], seenNormalized: Set<string> }}
 */
const ensureCategoryQueryTokenListAndSeenSet = (byQuery, queryName) => {
	if (!Array.isArray(byQuery[queryName])) {
		byQuery[queryName] = [];
	}
	const tokens = byQuery[queryName];
	const seenNormalized = buildSeenNormalizedSetForCategoryTokens(tokens);
	return { tokens, seenNormalized };
};

/**
 * @param {Element} wrapper
 * @param {string[]} tokens
 * @param {Set<string>} seenNormalized
 * @returns {void}
 */
const appendPartTerminologyCheckedSubFacetGroupTokens = (wrapper, tokens, seenNormalized) => {
	wrapper.querySelectorAll('.sub-facet-group input.multi-select-facet-checkbox').forEach((cb) => {
		if (cb.checked !== true) {
			return;
		}
		pushUniqueCategoryFacetToken(tokens, getCategoryFacetTokenFromInput(cb), seenNormalized);
	});
};

/**
 * Parent "select all subs" can leave subs unchecked in the DOM on first paint; still treat as selected.
 *
 * @param {Element} wrapper
 * @param {string[]} tokens
 * @param {Set<string>} seenNormalized
 * @returns {void}
 */
const appendPartTerminologySelectAllParentSubTokens = (wrapper, tokens, seenNormalized) => {
	wrapper.querySelectorAll(':scope > ul.facet-content > li').forEach((li) => {
		const subGroup = li.querySelector(':scope > ul.sub-facet-group');
		if (subGroup === null) {
			return;
		}
		const parentInput = li.querySelector(':scope > input.multi-select-facet-checkbox[type="checkbox"]');
		const label = li.querySelector(':scope > label');
		if (parentInput?.checked !== true) {
			return;
		}
		if (label?.classList.contains('partial')) {
			return;
		}
		subGroup.querySelectorAll('input.multi-select-facet-checkbox').forEach((cb) => {
			pushUniqueCategoryFacetToken(tokens, getCategoryFacetTokenFromInput(cb), seenNormalized);
		});
	});
};

/**
 * First navigation can leave category buckets only in computed facet values, not yet on every
 * sub-checkbox in the DOM. Merge getFacetValuesFromDom (flat + parent-collapse) into the snapshot.
 *
 * @param {Record<string, string[]>} byQuery
 * @param {string[]} categoryFacetTypes
 * @param {Record<string, unknown>|undefined} facetValues
 * @returns {void}
 */
const mergePartTerminologyFacetValuesIntoByQuery = (byQuery, categoryFacetTypes, facetValues) => {
	if (facetValues === null || typeof facetValues !== 'object') {
		return;
	}
	categoryFacetTypes.forEach((queryName) => {
		const rawVals = facetValues[queryName];
		if (!Array.isArray(rawVals) || rawVals.length === 0) {
			return;
		}
		const { tokens, seenNormalized } = ensureCategoryQueryTokenListAndSeenSet(byQuery, queryName);
		rawVals.forEach((item) => {
			if (typeof item === 'string' && item !== '') {
				pushUniqueCategoryFacetToken(tokens, item, seenNormalized);
			}
		});
	});
};

/**
 * @param {Record<string, string[]>} byQuery
 * @param {string[]} categoryFacetTypes
 * @param {Record<string, unknown>|null} lastConfirmed
 * @returns {void}
 */
const applyLastConfirmedPrimaryCategoryTokensToByQuery = (byQuery, categoryFacetTypes, lastConfirmed) => {
	if (lastConfirmed === null || typeof lastConfirmed !== 'object') {
		return;
	}
	categoryFacetTypes.forEach((qn) => {
		if (Array.isArray(byQuery[qn]) && byQuery[qn].length > 0) {
			return;
		}
		const arr = lastConfirmed[qn];
		if (Array.isArray(arr) && arr.length > 0) {
			byQuery[qn] = [...arr];
		}
	});
};

/**
 * Remember category sub-facet selections when Product Type is applied. Uses facet
 * tokens (data-url-name / name), not element ids, so restore still works after facet HTML is replaced.
 *
 * @param {boolean} useMobileFacets
 * @param {string[]} categoryFacetTypes
 * @param {Record<string, unknown>|null} [facetValuesFromChange] Tokens from {@link searchFacets.handleFacetChange}
 * after {@link searchFacets.getFacetValuesFromDom} (authoritative for the outgoing request on first paint).
 */
searchFacets.captureCategorySubFacetSnapshotForPartTerminology = function (
	useMobileFacets,
	categoryFacetTypes,
	facetValuesFromChange = null
) {
	if (!Array.isArray(categoryFacetTypes) || categoryFacetTypes.length === 0) {
		searchFacets._partTerminologyCategoryFacetValueSnapshot = null;

		return;
	}

	/** @type {Record<string, string[]>} */
	const byQuery = {};
	document.querySelectorAll('.search-facets-module').forEach((moduleEl) => {
		categoryFacetTypes.forEach((queryName) => {
			const escaped = escapeQueryNameForAttributeSelector(queryName);
			const wrapper = moduleEl.querySelector(`.facet-wrapper[data-query-name="${escaped}"]`);
			if (!wrapper) {
				return;
			}
			const { tokens, seenNormalized } = ensureCategoryQueryTokenListAndSeenSet(byQuery, queryName);
			appendPartTerminologyCheckedSubFacetGroupTokens(wrapper, tokens, seenNormalized);
			appendPartTerminologySelectAllParentSubTokens(wrapper, tokens, seenNormalized);
		});
	});

	try {
		mergePartTerminologyFacetValuesIntoByQuery(
			byQuery,
			categoryFacetTypes,
			searchFacets.getFacetValuesFromDom(useMobileFacets, false).facetValues
		);
		mergePartTerminologyFacetValuesIntoByQuery(
			byQuery,
			categoryFacetTypes,
			searchFacets.getFacetValuesFromDom(useMobileFacets, true).facetValues
		);
	} catch {
		// keep DOM-only snapshot
	}

	mergePartTerminologyFacetValuesIntoByQuery(
		byQuery,
		categoryFacetTypes,
		facetValuesFromChange
	);
	applyLastConfirmedPrimaryCategoryTokensToByQuery(
		byQuery,
		categoryFacetTypes,
		searchFacets._lastConfirmedPrimaryCategoryTokens
	);

	const keys = Object.keys(byQuery).filter((qn) => Array.isArray(byQuery[qn]) && byQuery[qn].length > 0);
	searchFacets._partTerminologyCategoryFacetValueSnapshot = keys.length > 0 ? byQuery : null;
};

/**
 * Sub-facet lists are often `hidden` until the nested arrow is expanded. After restoring
 * checkbox state, expand branches that contain checked subs so selections are visible.
 */
searchFacets.expandNestedCategoryBranchesWithCheckedSubs = function () {
	document.querySelectorAll('.search-facets-module ul.sub-facet-group').forEach((subUl) => {
		const hasChecked =
			subUl.querySelector(':scope input.multi-select-facet-checkbox[type="checkbox"]:checked') !== null;
		if (!hasChecked) {
			return;
		}
		subUl.removeAttribute('hidden');
		const parentLi = subUl.closest('li');
		if (parentLi === null) {
			return;
		}
		const arrow = parentLi.querySelector(':scope > .nested-facet-arrow');
		if (arrow !== null) {
			arrow.classList.add('expanded');
		}
	});
};

/**
 * Apply category sub-facet checkbox state from an in-memory snapshot (see post-response reconcile).
 *
 * @param {Record<string, string[]>} snap
 * @param {string[]} categoryFacetTypes
 */
searchFacets.applyCategoryFacetValueSnapshotFromObject = function (snap, categoryFacetTypes) {
	if (snap === null || typeof snap !== 'object') {
		return;
	}

	document.querySelectorAll('.search-facets-module').forEach((moduleEl) => {
		categoryFacetTypes.forEach((queryName) => {
			const tokens = snap[queryName];
			if (!Array.isArray(tokens) || tokens.length === 0) {
				return;
			}
			const wantedNormalized = new Set(
				tokens.map((t) => normalizeFacetValue(t)).filter((n) => n !== '')
			);
			if (wantedNormalized.size === 0) {
				return;
			}

			const escaped = escapeQueryNameForAttributeSelector(queryName);
			const wrapper = moduleEl.querySelector(`.facet-wrapper[data-query-name="${escaped}"]`);
			if (!wrapper) {
				return;
			}

			wrapper.querySelectorAll('input.multi-select-facet-checkbox[type="checkbox"]').forEach((cb) => {
				const urlName = cb.dataset.urlName;
				const name = cb.getAttribute('name');
				const shouldCheck = categoryFacetCheckboxMatchesTokens(wantedNormalized, urlName, name);
				cb.checked = shouldCheck;
				if (shouldCheck) {
					cb.setAttribute('checked', 'checked');
				} else {
					cb.removeAttribute('checked');
				}
			});
		});
	});

	searchFacets.handleCategoryCheckBoxIcon();
	searchFacets.expandNestedCategoryBranchesWithCheckedSubs();
};

/**
 * Restore category sub-facet checkboxes from {@link searchFacets._partTerminologyCategoryFacetValueSnapshot}.
 *
 * @param {string[]} categoryFacetTypes
 */
searchFacets.applyCategoryFacetValueSnapshotForPartTerminology = function (categoryFacetTypes) {
	const snap = searchFacets._partTerminologyCategoryFacetValueSnapshot;
	if (snap === null || typeof snap !== 'object') {
		return;
	}

	searchFacets._partTerminologyCategoryFacetValueSnapshot = null;

	searchFacets.applyCategoryFacetValueSnapshotFromObject(snap, categoryFacetTypes);
};

/**
 * Clearing part terminology can leave category sub-facet checkboxes on a deep path. For each
 * category facet branch that was still active (checked or partial), clear subs then re-select all subs
 * so getFacetValuesFromDom(..., true) emits parent-level category buckets again.
 *
 * @param {string[]} categoryFacetTypes
 */
searchFacets.restoreCategoryTreesAfterPartTerminologyCleared = function (categoryFacetTypes) {
	if (!Array.isArray(categoryFacetTypes) || categoryFacetTypes.length === 0) {
		return;
	}

	document.querySelectorAll('.search-facets-module').forEach((moduleEl) => {
		categoryFacetTypes.forEach((queryName) => {
			const escaped = escapeQueryNameForAttributeSelector(queryName);
			const wrapper = moduleEl.querySelector(`.facet-wrapper[data-query-name="${escaped}"]`);
			if (!wrapper) {
				return;
			}

			wrapper.querySelectorAll(':scope > ul.facet-content > li').forEach((li) => {
				const subGroup = li.querySelector(':scope > ul.sub-facet-group');
				if (!subGroup) {
					return;
				}

				const parentInput = li.querySelector(':scope > input.multi-select-facet-checkbox');
				const label = li.querySelector(':scope > label');
				if (!parentInput) {
					return;
				}

				const keepBranch =
					parentInput.checked === true ||
					label?.classList?.contains('partial');

				subGroup.querySelectorAll('input.multi-select-facet-checkbox').forEach((sub) => {
					sub.checked = false;
					sub.removeAttribute('checked');
				});

				if (keepBranch) {
					subGroup.querySelectorAll('input.multi-select-facet-checkbox').forEach((sub) => {
						sub.checked = true;
						sub.setAttribute('checked', 'checked');
					});
					parentInput.checked = true;
					parentInput.setAttribute('checked', 'checked');
					if (label !== null) {
						label.classList.remove('partial');
					}
				}
			});
		});
	});

	searchFacets.handleCategoryCheckBoxIcon();
	searchFacets.expandNestedCategoryBranchesWithCheckedSubs();
};

searchFacets.handleFacetChange = function (useMobileFacets, category = '', identifierName = '', isChecked = false, use_cfc = false) {

	const $facetsModule = searchFacets.getFacetsModule();
	const categoryFacetTypes = searchFacets.getCategoryFacetTypesFromDom();
	const isCategoryFacet = (name) => categoryFacetTypes.includes(name);
	const isPrimaryFacet = (name) => PRIMARY_FACET_QUERY_NAMES.includes(name);
	const initialIdentifierName = identifierName;
	const hasVehicleAttr = $facetsModule.data('has-vehicle');
	const hasVehicleSelected = hasVehicleAttr === 1 || hasVehicleAttr === '1' || hasVehicleAttr === true;

	if (identifierName !== '' && isCategoryFacet(identifierName)) {
		searchFacets._partTerminologyCategoryFacetValueSnapshot = null;
		searchFacets._categoryFacetDomReconcileSnapshot = null;
		searchFacets._lastConfirmedPrimaryCategoryTokens = null;
	}

	if (isCategoryFacet(identifierName)) {
		searchFacets.handleCategoryFacetsSelection(category);
	}

	const restoringAfterPartTerminologyClear =
		isChecked === false && isPartTerminologyFacetIdentifier(identifierName);

	/**
	 * Part-terminology uncheck: category restore snapshot is built by merging, in order,
	 * searchFacets._partTerminologyCategoryFacetValueSnapshot (first) and
	 * searchFacets._lastConfirmedPrimaryCategoryTokens (only fills a query name when the merged map has no
	 * tokens for that name yet). If the merged result is non-empty, we apply the value snapshot to the
	 * DOM, set _categoryFacetDomReconcileSnapshot, and read with parent-bucket mode off; otherwise
	 * restoreCategoryTreesAfterPartTerminologyCleared and read with parent buckets. mergeCategoryRestoreSnapshotIntoFacetValues
	 * re-applies that snapshot to facetValues after the initial getFacetValuesFromDom and again after
	 * the optional secondary-facet recompute. A direct category facet interaction (above) clears the three
	 * in-memory snapshot fields.
	 */
	/** @type {Record<string, string[]>|null} */
	let categoryFacetRestoreSnapshot = null;
	if (restoringAfterPartTerminologyClear) {
		const rawPt = searchFacets._partTerminologyCategoryFacetValueSnapshot;
		const rawLc = searchFacets._lastConfirmedPrimaryCategoryTokens;
		/** @type {Record<string, string[]>} */
		const merged = {};
		const mergeCategoryTokens = (raw) => {
			if (raw === null || typeof raw !== 'object') {
				return;
			}
			categoryFacetTypes.forEach((qn) => {
				const arr = raw[qn];
				if (!Array.isArray(arr) || arr.length === 0) {
					return;
				}
				if (!Array.isArray(merged[qn]) || merged[qn].length === 0) {
					merged[qn] = [...arr];
				}
			});
		};
		mergeCategoryTokens(rawPt);
		mergeCategoryTokens(rawLc);
		if (Object.keys(merged).length > 0) {
			categoryFacetRestoreSnapshot = merged;
		}
	}

	let useParentCategoryBucketsForDom = false;
	if (restoringAfterPartTerminologyClear) {
		const hadValueSnapshot = categoryFacetRestoreSnapshot !== null;

		if (hadValueSnapshot) {
			searchFacets.applyCategoryFacetValueSnapshotForPartTerminology(categoryFacetTypes);
			searchFacets._categoryFacetDomReconcileSnapshot = {};
			Object.keys(categoryFacetRestoreSnapshot).forEach((qn) => {
				const arr = categoryFacetRestoreSnapshot[qn];
				if (Array.isArray(arr) && arr.length > 0) {
					searchFacets._categoryFacetDomReconcileSnapshot[qn] = [...arr];
				}
			});
		} else {
			searchFacets.restoreCategoryTreesAfterPartTerminologyCleared(categoryFacetTypes);
			useParentCategoryBucketsForDom = true;
		}
	}

	let { facetValues } = searchFacets.getFacetValuesFromDom(
		useMobileFacets,
		useParentCategoryBucketsForDom
	);

	mergeCategoryRestoreSnapshotIntoFacetValues(facetValues, categoryFacetRestoreSnapshot);

	const hasPrimarySelected = Object.keys(facetValues).some((name) => {
		if (!isPrimaryFacet(name)) {
			return false;
		}
		const values = facetValues[name];
		return Array.isArray(values) ? values.length > 0 : !!values;
	});

	const shouldKeepSecondaryFacets = hasPrimarySelected && hasVehicleSelected;

	if (!shouldKeepSecondaryFacets) {
		const preservedFacetNames = new Set([
			...PINNED_FACET_QUERY_NAMES,
			...VEHICLE_FACET_QUERY_NAMES,
		]);
		if (isChecked && identifierName) {
			preservedFacetNames.add(identifierName);
			// When adding a selection, preserve all other facet types that currently have selections
			// so e.g. availability is kept in the request when clicking category facets.
			Object.keys(facetValues).forEach((name) => preservedFacetNames.add(name));
		}

		// Only clear other facets when user is *checking* an option. When unchecking, leave all other
		// checkboxes as-is (DOM already reflects the single uncheck); avoids wiping other multi-select
		// selections that share the same data-query-name (e.g. autocareCategories, availability).
		if (isChecked) {
			const multiSelectFacetCheckboxes = useMobileFacets
				? searchFacets.getMobileFacetCheckboxes()
				: searchFacets.getFacetCheckboxes();

			multiSelectFacetCheckboxes.forEach((checkbox) => {
				const queryName = checkbox.getAttribute('data-query-name');
				// Never uncheck other options within the same multi-select facet (e.g. keep all category
				// selections when user checks another category).
				if (queryName === identifierName) {
					return;
				}
				if (!preservedFacetNames.has(queryName)) {
					checkbox.checked = false;
				}
			});

			// Recompute facet values after clearing secondary facets
			({ facetValues } = searchFacets.getFacetValuesFromDom(useMobileFacets));
		}
	}

	mergeCategoryRestoreSnapshotIntoFacetValues(facetValues, categoryFacetRestoreSnapshot);

	if (isChecked === true && isPartTerminologyFacetIdentifier(identifierName)) {
		/** @type {Record<string, string[]>|null} */
		let categoryTokensFromFacetValues = null;
		categoryFacetTypes.forEach((qn) => {
			const v = facetValues[qn];
			if (Array.isArray(v) && v.length > 0) {
				if (categoryTokensFromFacetValues === null) {
					categoryTokensFromFacetValues = {};
				}
				categoryTokensFromFacetValues[qn] = [...v];
			}
		});
		searchFacets.captureCategorySubFacetSnapshotForPartTerminology(
			useMobileFacets,
			categoryFacetTypes,
			categoryTokensFromFacetValues
		);
	}

	if (!isChecked && isPartTerminologyFacetIdentifier(initialIdentifierName)) {
		const stillHasPartTerminology = PART_TERMINOLOGY_FACET_QUERY_NAMES.some((n) => {
			const v = facetValues[n];
			return Array.isArray(v) ? v.length > 0 : !!v;
		});
		if (!stillHasPartTerminology) {
			identifierName = '';
		}
	} else if (!facetValues[identifierName]) {
		if (!isChecked) {
			identifierName = '';
		}
	}

	// Append search str. Append DSEO params if necessary
	const pageId = $facetsModule.data('page-id');
	const pageUrl = $facetsModule.data('page-url');
	const searchStr = $facetsModule.data('search-str');
	if (pageId) { facetValues.page_id = pageId; }
	if (pageUrl) { facetValues.page_url = pageUrl; }
	// SHOP-4301: Prevent leaking checkbox ids (e.g. category=desktop_autocareCategories_*) into the URL when toggling facets.
	// Only include the `category` query param for category-tree selection (CFC link click), not for facet checkbox changes.
	if (category && initialIdentifierName === '') { facetValues.category = category; }
	const requestParams = { 
		...facetValues,
		search_str: searchStr,
		identifier: {
			identifier: identifierName,
			usePreviousFacets: false
		},
		use_cfc: use_cfc,
	};

	if (!restoringAfterPartTerminologyClear) {
		searchFacets._lastConfirmedPrimaryCategoryTokens = null;
		categoryFacetTypes.forEach((qn) => {
			const v = facetValues[qn];
			if (Array.isArray(v) && v.length > 0) {
				if (searchFacets._lastConfirmedPrimaryCategoryTokens === null) {
					searchFacets._lastConfirmedPrimaryCategoryTokens = {};
				}
				searchFacets._lastConfirmedPrimaryCategoryTokens[qn] = [...v];
			}
		});
	}

	$facetsModule.trigger('selectedFacetBucketsChanged', [requestParams]);
	searchFacets.disableFacets();
};

searchFacets.handleCategoryFacetsSelection = function (category) {
	let categoryCheckbox = rp$(`#${category}`);
	let subFacetsGroup = categoryCheckbox.next().next().next();
	let subFacets = subFacetsGroup.find('input[type=checkbox]');

	// Use the same logic for both desktop and mobile
	//handle Select/deselect all category's subfacets
	subFacets.each((idx, checkbox) => {
		checkbox.checked = categoryCheckbox[0] ? categoryCheckbox[0].checked : false;
	});

	// handle subfacet selection
	if (subFacets.length === 0) {
		if (categoryCheckbox.prop('checked')) {
			const parentFacetSelected = categoryCheckbox.parent().parent().prev().prev().prev();
			parentFacetSelected.attr('checked', true);
		}
	}
};

searchFacets.showMoreFacets = function (element) {
	const keyWord = element.id.replace(/see-more-(mobile|desktop)-/, '');
	document.querySelectorAll('.hidden-filter-' + keyWord).forEach(function (el) {
		el.removeAttribute('hidden');
	});

	document.querySelectorAll(`#see-less-mobile-${keyWord}, #see-less-desktop-${keyWord}`).forEach(function (el) {
		el.removeAttribute('hidden');
		const parentLi = el.closest('li');
		if (parentLi) {
			parentLi.removeAttribute('hidden');
		}
	});
	element.setAttribute('hidden',true);
	const parentLi = element.closest('li');
	if (parentLi) {
		parentLi.setAttribute('hidden', true);
	}

	searchFacets.saveExpandedState(keyWord, true);
};

searchFacets.showLessFacets = function (element){
	const keyWord = element.id.replace(/see-less-(mobile|desktop)-/, '');
	document.querySelectorAll('.hidden-filter-' + keyWord).forEach(function(el) {
		el.setAttribute('hidden', true);
	});

	document.querySelectorAll(`#see-more-mobile-${keyWord}, #see-more-desktop-${keyWord}`).forEach(function (el) {
		el.removeAttribute('hidden');
	});
	element.setAttribute('hidden', true);

	searchFacets.saveExpandedState(keyWord, false);
};

searchFacets.showLessFacets = function (element) {
	const keyWord = element.id.replace(/see-less-(mobile|desktop)-/, '');
	document.querySelectorAll('.hidden-filter-' + keyWord).forEach(function (el) {
		el.setAttribute('hidden', true);
	});

	document.querySelectorAll(`#see-more-mobile-${keyWord}, #see-more-desktop-${keyWord}`).forEach(function (el) {
		el.removeAttribute('hidden');
		const parentLi = el.closest('li');
		if (parentLi) {
			parentLi.removeAttribute('hidden');
		}
	});
	element.setAttribute('hidden', true);
	const parentLi = element.closest('li');
	if (parentLi) {
		parentLi.setAttribute('hidden', true);
	}
};

searchFacets.getFacetCheckboxes = function () {
	return Array.from(
		document.querySelectorAll('.search-facets-module .multi-select-facet-checkbox.desktop')
	);
};

searchFacets.getMobileFacetCheckboxes = function () {
	return Array.from(
		document.querySelectorAll('.search-facets-module .multi-select-facet-checkbox.mobile')
	);
};

searchFacets.handleParentCategoryExpand = function () {
	const expandCategoryArrows = rp$('.nested-facet-arrow');

	if (!expandCategoryArrows) return;

	expandCategoryArrows.each((_, arrow) => {
		const target = arrow.getAttribute('data-target');

		arrow.removeEventListener('click', arrow._expandHandler);

		arrow._expandHandler = (e) => {
			e.stopPropagation();
			e.preventDefault();

			const subCategorySection = rp$(`[data-url-name="${target}"]`);
			if (subCategorySection) {
				subCategorySection.each((_, section) => {
					section.toggleAttribute('hidden');
				});
			}

			arrow.classList.toggle('expanded');
		};

		arrow.addEventListener('click', arrow._expandHandler);
	});
};

function shouldRunSearchEnhancements() {
	return document.querySelector('.search-filters-module, .search-facets-module') !== null;
}

searchFacets.prepareFacets = function () {
	searchFacets.getCategoryFacetTypesFromDom();

	const facetsModule = searchFacets.getFacetsModule();
	const multiSelectFacetCheckboxes = searchFacets.getFacetCheckboxes();
	const multiSelectFacetMobileCheckboxes = searchFacets.getMobileFacetCheckboxes();

	if (shouldRunSearchEnhancements()) {
		const searchListingModule = document.querySelector('.search-listing-module');
		if (searchListingModule && !searchListingModule.classList.contains('search-based-v-page')) {
			searchListingModule.classList.add('search-based-v-page');
		}

		const searchFiltersModule = document.querySelector('.search-filters-module');
		if (searchFiltersModule && !searchFiltersModule.classList.contains('search-based-v-page')) {
			searchFiltersModule.classList.add('search-based-v-page');
		}

		const desktopSearchFilters = document.querySelector('.desktop-search-filters');
		if (desktopSearchFilters && !desktopSearchFilters.classList.contains('search-based-v-page')) {
			desktopSearchFilters.classList.add('search-based-v-page');
		}

		const filtersOverlay = document.querySelector('#filters-overlay, .filters-overlay');
		if (filtersOverlay && !filtersOverlay.classList.contains('search-based-v-page')) {
			filtersOverlay.classList.add('search-based-v-page');
		}

		const searchFacetsModules = document.querySelectorAll('.search-facets-module');
		searchFacetsModules.forEach(module => {
			if (!module.classList.contains('search-based-v-page')) {
				module.classList.add('search-based-v-page');
			}
		});

		const searchContainer = document.querySelector('.search-container, .search-page, .products-page, .main-content');
		if (searchContainer && !searchContainer.classList.contains('search-based-v-page')) {
			searchContainer.classList.add('search-based-v-page');
		}
		
		searchFacets.handleCategoryCheckBoxIcon();
		searchFacets.expandNestedCategoryBranchesWithCheckedSubs();
		searchFacets.initializeCollapsibleFacets();
		searchFacets.updateAppliedFiltersPills();
		searchFacets.initializeClearAllButton();
		searchFacets.initializePillsScroll();

		// Only register global event handlers once to prevent multiple registrations
		if (!searchFacets._eventHandlersInitialized) {
			searchFacets.initializeAppliedFilters();

			const vehiclePickers = document.querySelectorAll('.refine-results select, .vehicle-picker select, select[name*="make"], select[name*="model"], select[name*="year"]');
			vehiclePickers.forEach(select => {
				select.removeEventListener('change', select._vehicleChangeHandler);
				select._vehicleChangeHandler = () => {
					setTimeout(() => {
						searchFacets.updateAppliedFiltersPills();
					}, 100);
				};
				select.addEventListener('change', select._vehicleChangeHandler);
			});

			rp$(document).on('searchResults:response', (e, response = {}) => {
				const facetsData = response.selected_facets || response.facets || response.filters;
				if (response && facetsData) {
					searchFacets.updateSelectedFacetCheckboxes(facetsData);
				}
				// Listing replaces facet HTML then runs prepareFacets; selected_facets can be incomplete.
				// Reconcile: next tick, then again right after prepareFacets (collapsible/expand runs on real checked state).
				const runCategoryFacetDomReconcilePass = (clearAfter) => {
					if (searchFacets._categoryFacetDomReconcileSnapshot === null) {
						return;
					}
					const pending = searchFacets._categoryFacetDomReconcileSnapshot;
					if (clearAfter === true) {
						searchFacets._categoryFacetDomReconcileSnapshot = null;
					}
					searchFacets.applyCategoryFacetValueSnapshotFromObject(
						pending,
						searchFacets.getCategoryFacetTypesFromDom()
					);
				};
				setTimeout(() => {
					runCategoryFacetDomReconcilePass(false);
				}, 0);
				if (response && facetsData) {
					searchFacets.enableFacets();

					setTimeout(() => {
						searchFacets.prepareFacets();
						runCategoryFacetDomReconcilePass(false);
						setTimeout(() => {
							runCategoryFacetDomReconcilePass(true);
						}, 40);
					}, 50);
				} else {
					setTimeout(() => {
						runCategoryFacetDomReconcilePass(true);
					}, 85);
				}
			});

			rp$(document).on('searchResults:request', () => {
				searchFacets.updateAppliedFiltersPills();
			});

			rp$(document).ready(() => {
				const searchListingModuleReady = document.querySelector('.search-listing-module');
				if (searchListingModuleReady) {
					setTimeout(() => {
						searchFacets.updateAppliedFiltersPills();
					}, 200);
				}
			});

			window.updateAppliedFiltersTitle = function () {
				searchFacets.updateAppliedFiltersPills();
			};

			const titleObserver = new MutationObserver((mutations) => {
				mutations.forEach((mutation) => {
					if (mutation.type === 'childList' || mutation.type === 'characterData') {
						const target = mutation.target;
						if (target.textContent && target.textContent.includes('+')) {
							target.textContent = target.textContent.replace(/\+/g, ' ');
						}
					}
				});
			});

			const titleElements = document.querySelectorAll('h1, h2, h3, .page-title, .results-title, #applied-filters-title');
			titleElements.forEach(element => {
				titleObserver.observe(element, {
					childList: true,
					characterData: true,
					subtree: true
				});
			});

			searchFacets._eventHandlersInitialized = true;
		}
	}

	const mobileFiltersApplyButton = rp$('#mobile-filters-apply-button');
	const mobileFiltersClearButton = rp$('#mobile-filters-clear-button');

	if (facetsModule) {

		const showMore = Array.from(
			document.querySelectorAll('.show-more')
		);
		showMore.forEach((showMoreButton) => {
			showMoreButton.addEventListener('click', () => searchFacets.showMoreFacets(showMoreButton));
		});

		const showLess = Array.from(
			document.querySelectorAll('.show-less')
		);

		showLess.forEach((showLessButton) => {
			showLessButton.addEventListener('click', () => searchFacets.showLessFacets(showLessButton));
		});

		multiSelectFacetCheckboxes.forEach((checkbox) => {
			checkbox.removeEventListener('change', checkbox._facetChangeHandler);

			checkbox._facetChangeHandler = () => {
				searchFacets.handleFacetChange(false, checkbox.getAttribute('id'), checkbox.getAttribute('data-query-name'), checkbox.checked);
			};

			checkbox.addEventListener('change', checkbox._facetChangeHandler);
		});

		multiSelectFacetMobileCheckboxes.forEach((checkbox) => {
			checkbox.removeEventListener('change', checkbox._facetChangeHandler);

			checkbox._facetChangeHandler = () => {
				searchFacets.handleFacetChange(true, checkbox.getAttribute('id'), checkbox.getAttribute('data-query-name'), checkbox.checked);
			};

			checkbox.addEventListener('change', checkbox._facetChangeHandler);
		});

		if (mobileFiltersApplyButton && mobileFiltersApplyButton.length) {
			rp$('#mobile-filters-apply-button').on('click', () => {
				multiSelectFacetMobileCheckboxes.forEach((checkbox) => {
					if (checkbox.checked) {
						searchFacets.handleFacetChange(true, checkbox.getAttribute('id'), checkbox.getAttribute('data-query-name'), checkbox.checked);
					}
				});
			});
		}

		if (mobileFiltersClearButton && mobileFiltersClearButton.length) {
			rp$('#mobile-filters-clear-button').on('click', () => { searchFacets.clearAllFilters(); });
		}
	}

	searchFacets.handleParentCategoryExpand();

	searchFacets.restoreExpandedStates();
};

searchFacets.getFacetValuesFromDom = function (useMobileFacets = false, showCategoryIfAllSubcategoriesSelected = false) {
	const multiSelectFacetCheckboxes = useMobileFacets ? searchFacets.getMobileFacetCheckboxes() : searchFacets.getFacetCheckboxes();
	const categoryFacetTypes = searchFacets.getCategoryFacetTypesFromDom();
	const isCategoryFacet = (name) => categoryFacetTypes.includes(name);

	const facetValues = {};
	multiSelectFacetCheckboxes.forEach(checkbox => {
		if (checkbox.checked) {
			const queryName = checkbox.getAttribute('data-query-name');
			const checkboxName = checkbox.getAttribute('name');
			const urlName = checkbox.dataset.urlName;

			const valueToUse = urlName || checkboxName;

			const isSubFacet = checkboxName.includes('%3E') || checkbox.closest('.sub-facet-group');
			const isCategory = isCategoryFacet(queryName);
			const parentLi = checkbox.closest('li');
			const subFacetCheckboxCount = parentLi === null
				? 0
				: parentLi.querySelectorAll('.sub-facet-group > li > input.multi-select-facet-checkbox').length;
			let allSubcategoriesSelected = false;
			let allSiblingsSelected = false;

			if (!isSubFacet && isCategory) {
				allSubcategoriesSelected = true;

				rp$(checkbox)
					.closest('li')
					.find('.sub-facet-group > li > input.multi-select-facet-checkbox')
					.each((idx, subFacet) => {
						if (!subFacet.checked) {
							allSubcategoriesSelected = false;
						}
					});
			} else if (isSubFacet) {
				allSiblingsSelected = true;

				rp$(checkbox).closest('.sub-facet-group').find('li >input.multi-select-facet-checkbox').each((idx, subFacet) => {
					if (!subFacet.checked) {
						allSiblingsSelected = false;
					}
				});
			}

			const isCollapsedCategoryWithoutSubs = !isSubFacet && isCategory && subFacetCheckboxCount === 0;
			const isAllSubcategoriesRollup = showCategoryIfAllSubcategoriesSelected && allSubcategoriesSelected;
			const isLeafSelection =
				(isSubFacet || !isCategoryFacet(queryName)) &&
				(!showCategoryIfAllSubcategoriesSelected || !allSiblingsSelected);

			if (isCollapsedCategoryWithoutSubs || isAllSubcategoriesRollup || isLeafSelection) {
				if (!facetValues[queryName]) {
					facetValues[queryName] = [];
				}
				facetValues[queryName].push(valueToUse);
			}
		}
	});

	return { facetValues };
};

searchFacets.getAvailableSearchFacetQueryNames = function () {
	const facets = Array.from(
		document.querySelectorAll('.search-facets-module .facet-wrapper')
	);

	return facets.map((facet) => facet.getAttribute('data-query-name'));
};

searchFacets.updateSelectedFacetCheckboxes = function (selectedFacetData) {
	const selectedFacets = isEmpty(selectedFacetData) ? {} : selectedFacetData;
	const categoryFacetTypes = searchFacets.getCategoryFacetTypesFromDom();
	const apply = (checkbox) => {
		const checkboxName = checkbox.getAttribute('name');
		const checkboxUrlName = checkbox.dataset.urlName;
		const checkboxQueryName = checkbox.dataset.queryName;
		const bucketsRaw = get(selectedFacets, `[${checkboxQueryName}]`, []);
		const buckets = Array.isArray(bucketsRaw) ? bucketsRaw : [bucketsRaw];
		const normalizedBuckets = new Set(
			buckets
				.map(normalizeFacetValue)
				.filter((b) => typeof b === 'string' && b !== '')
		);

		const isCategory =
			typeof checkboxQueryName === 'string' && checkboxQueryName !== '' && categoryFacetTypes.includes(checkboxQueryName);
		let on = false;
		if (isCategory) {
			// Collapsed parent tokens (e.g. ["Engine"]) must still check all sub-rows under that parent.
			on = categoryFacetCheckboxMatchesTokens(normalizedBuckets, checkboxUrlName, checkboxName);
		} else {
			const candidates = [checkboxName, checkboxUrlName]
				.filter((value) => typeof value === 'string' && value !== '')
				.map(normalizeFacetValue);
			const normalizedCheckboxName = normalizeFacetValue(checkboxName);
			const isChildMatch = candidates.some((value) => normalizedBuckets.has(value));
			const isParentMatch = Array.from(normalizedBuckets).some((bucket) =>
				bucket.startsWith(`${normalizedCheckboxName}>`)
			);
			on = isChildMatch || isParentMatch;
		}
		checkbox.checked = on;
		if (on) {
			checkbox.setAttribute('checked', 'checked');
		} else {
			checkbox.removeAttribute('checked');
		}
	};
	searchFacets.getFacetCheckboxes().forEach(apply);
	searchFacets.getMobileFacetCheckboxes().forEach(apply);
	searchFacets.handleCategoryCheckBoxIcon();
	searchFacets.expandNestedCategoryBranchesWithCheckedSubs();
};

searchFacets.clearFacetCheckboxes = function (useMobileFacets = false) {
	const checkboxes = useMobileFacets
		? searchFacets.getMobileFacetCheckboxes()
		: searchFacets.getFacetCheckboxes();

	if (checkboxes && checkboxes.length) {
		checkboxes.forEach((checkbox) => {
			checkbox.checked = false;
		});
	}
};

searchFacets.handleCategoryCheckBoxIcon = function () {
	document.querySelectorAll('.search-facets-module .nested-facet-arrow').forEach((arrowButton) => {
		const parentLi = arrowButton.closest('li');
		if (parentLi === null) {
			return;
		}
		const inputCheckbox = parentLi.querySelector(':scope > input.multi-select-facet-checkbox[type="checkbox"]');
		const label = parentLi.querySelector(':scope > label');
		const subUl = parentLi.querySelector(':scope > ul.sub-facet-group');
		if (inputCheckbox === null || subUl === null) {
			return;
		}
		const subInputs = Array.from(
			subUl.querySelectorAll(':scope > li > input.multi-select-facet-checkbox[type="checkbox"]')
		);
		if (subInputs.length === 0) {
			return;
		}
		const selectedCount = subInputs.filter((sub) => sub.checked === true).length;
		const total = subInputs.length;

		if (selectedCount === 0) {
			inputCheckbox.checked = false;
			inputCheckbox.removeAttribute('checked');
			if (label !== null) {
				label.classList.remove('partial');
			}
		} else if (selectedCount < total) {
			inputCheckbox.checked = true;
			inputCheckbox.setAttribute('checked', 'checked');
			if (label !== null) {
				label.classList.add('partial');
			}
		} else {
			inputCheckbox.checked = true;
			inputCheckbox.setAttribute('checked', 'checked');
			if (label !== null) {
				label.classList.remove('partial');
			}
		}
	});
};

function setFacetsInteractiveState(enabled) {
	const checkboxes = searchFacets.getFacetCheckboxes();
	checkboxes.forEach((checkbox) => {
		checkbox.disabled = !enabled;
	});
	document.querySelectorAll('.search-facets-module .category-facet-wrapper').forEach((wrapper) => {
		if (enabled) {
			wrapper.classList.remove('disabled');
		} else {
			wrapper.classList.add('disabled');
		}
	});
}

searchFacets.disableFacets = function () { setFacetsInteractiveState(false); };
searchFacets.enableFacets = function () { setFacetsInteractiveState(true); };

searchFacets.initializeCollapsibleFacets = function () {
	// Allow custom sites to opt-out of default accordion behavior
	// by adding data-disable-default-accordion="true" to .search-facets-module
	const facetsModule = document.querySelector('.search-facets-module');
	if (facetsModule && facetsModule.getAttribute('data-disable-default-accordion') === 'true') {
		return;
	}

	const collapsibleFacets = document.querySelectorAll('.collapsible-facet');

	collapsibleFacets.forEach((facet) => {
		const header = facet.querySelector('.facet-header');
		const toggleBtn = facet.querySelector('.facet-toggle-btn');
		const content = facet.querySelector('.facet-content');

		if (header && toggleBtn && content) {
			const hasSelectedItems = content.querySelectorAll('input[type="checkbox"]:checked').length > 0;

			if (hasSelectedItems || facet.getAttribute('data-default-expanded') === 'true') {
				facet.setAttribute('data-collapsed', 'false');
				content.hidden = false;
				toggleBtn.classList.add('expanded');
			} else {
				facet.setAttribute('data-collapsed', 'true');
				content.hidden = true;
				toggleBtn.classList.remove('expanded');
			}

			if (header._toggleHandler) {
				header.removeEventListener('click', header._toggleHandler);
			}

			header._toggleHandler = (e) => {
				if (e.target.type === 'checkbox' || e.target.tagName === 'LABEL') {
					return;
				}

				const isCollapsed = facet.getAttribute('data-collapsed') === 'true';

				if (isCollapsed) {
					// Expand
					facet.setAttribute('data-collapsed', 'false');
					content.hidden = false;
					toggleBtn.classList.add('expanded');
				} else {
					// Collapse
					facet.setAttribute('data-collapsed', 'true');
					content.hidden = true;
					toggleBtn.classList.remove('expanded');
				}
			};

			header.addEventListener('click', header._toggleHandler);

			const checkboxes = content.querySelectorAll('input[type="checkbox"]');
			checkboxes.forEach(checkbox => {
				if (checkbox._clickStopHandler) {
					checkbox.removeEventListener('click', checkbox._clickStopHandler);
				}
				checkbox._clickStopHandler = (e) => {
					e.stopPropagation();
				};
				checkbox.addEventListener('click', checkbox._clickStopHandler);
			});
		}
	});
};

searchFacets.initializeAppliedFilters = function () {
	searchFacets.updateAppliedFiltersPills();


	setTimeout(() => {
		searchFacets.updateAppliedFiltersPills();
	}, 100);
};

searchFacets.initializeClearAllButton = function () {
	const searchListingModule = document.querySelector('.search-listing-module');
	if (!searchListingModule) return;

	const clearAllBtn = searchListingModule.querySelector('.clear-all-filters-btn');
	if (clearAllBtn) {
		if (clearAllBtn._clearAllHandler) {
			clearAllBtn.removeEventListener('click', clearAllBtn._clearAllHandler);
		}

		clearAllBtn._clearAllHandler = () => {
			searchFacets.clearAllFilters();
		};

		clearAllBtn.addEventListener('click', clearAllBtn._clearAllHandler);
	}
};

searchFacets.initializePillsScroll = function () {
	const searchListingModule = document.querySelector('.search-listing-module');
	if (!searchListingModule) return;

	const scrollButtons = searchListingModule.querySelectorAll('.pills-scroll-btn');

	scrollButtons.forEach(btn => {
		if (btn._scrollHandler) {
			btn.removeEventListener('click', btn._scrollHandler);
		}

		btn._scrollHandler = () => {
			const target = btn.getAttribute('data-target');
			const isLeft = btn.classList.contains('scroll-left');
			const pillsContainer = searchListingModule.querySelector(`#applied-filters-pills-${target}`);

			if (pillsContainer) {
				const scrollAmount = 200; // Scroll 200px at a time
				if (isLeft) {
					pillsContainer.scrollLeft -= scrollAmount;
				} else {
					pillsContainer.scrollLeft += scrollAmount;
				}

				setTimeout(() => {
					searchFacets.updatePillsScrollButtons(pillsContainer, target);
				}, 300);
			}
		};

		btn.addEventListener('click', btn._scrollHandler);
	});

	const pillsContainers = searchListingModule.querySelectorAll('.applied-filters-pills');
	pillsContainers.forEach(container => {
		const target = container.id.includes('mobile') ? 'mobile' : 'desktop';

		if (container._scrollHandler) {
			container.removeEventListener('scroll', container._scrollHandler);
		}

		container._scrollHandler = () => {
			searchFacets.updatePillsScrollButtons(container, target);
		};

		container.addEventListener('scroll', container._scrollHandler);

		searchFacets.updatePillsScrollButtons(container, target);
	});

	if (!window._pillsScrollResizeHandlerAttached) {
		window._pillsScrollResizeHandler = () => {
			const searchListingMod = document.querySelector('.search-listing-module');
			if (!searchListingMod) return;

			const containers = searchListingMod.querySelectorAll('.applied-filters-pills');
			containers.forEach(container => {
				const target = container.id.includes('mobile') ? 'mobile' : 'desktop';
				searchFacets.updatePillsScrollButtons(container, target);
			});
		};
		window.addEventListener('resize', window._pillsScrollResizeHandler);
		window._pillsScrollResizeHandlerAttached = true;
	}
};

searchFacets.updatePillsScrollButtons = function (pillsContainer, target) {
	const searchListingModule = document.querySelector('.search-listing-module');
	if (!searchListingModule) return;

	const leftBtn = searchListingModule.querySelector(`.pills-scroll-btn.scroll-left.${target}-only`);
	const rightBtn = searchListingModule.querySelector(`.pills-scroll-btn.scroll-right.${target}-only`);

	if (!leftBtn || !rightBtn) return;

	const isAtStart = pillsContainer.scrollLeft <= 0;
	const isAtEnd = pillsContainer.scrollLeft >= pillsContainer.scrollWidth - pillsContainer.clientWidth - 1;
	const canScroll = pillsContainer.scrollWidth > pillsContainer.clientWidth;

	// hide the buttons if there's nothing to scroll
	leftBtn.style.display = rightBtn.style.display = canScroll ? 'flex' : 'none';

	if (canScroll) {
		leftBtn.disabled = isAtStart;
		rightBtn.disabled = isAtEnd;
	}
};

searchFacets.getVehicleInformation = function () {
	let vehicleInfo = {};
	
	//  Check URL path first (most reliable for vehicle-specific pages)
	const pathMatch = window.location.pathname.match(/\/v\/(\d{4})\/([^/]+)\/([^/]+)/);
	if (pathMatch) {
		vehicleInfo.year = pathMatch[1];
		vehicleInfo.make = decodeURIComponent(pathMatch[2]).replace(/\+/g, ' ');
		vehicleInfo.model = decodeURIComponent(pathMatch[3]).replace(/\+/g, ' ');
		return vehicleInfo;
	}
	
	// Check for DSEO-specific URL patterns
	const dseoPathMatch = window.location.pathname.match(/\/(\d{4})-([^/]+)-([^/]+)/);
	if (dseoPathMatch) {
		vehicleInfo.year = dseoPathMatch[1];
		vehicleInfo.make = decodeURIComponent(dseoPathMatch[2]).replace(/\+/g, ' ');
		vehicleInfo.model = decodeURIComponent(dseoPathMatch[3]).replace(/\+/g, ' ');
		return vehicleInfo;
	}
	
	//  Check URL parameters
	const urlParams = new URLSearchParams(window.location.search);
	const urlYear = urlParams.get('year');
	const urlMake = urlParams.get('make');
	const urlModel = urlParams.get('model');
	
	if (urlYear && urlMake && urlModel) {
		vehicleInfo.year = urlYear;
		vehicleInfo.make = decodeURIComponent(urlMake).replace(/\+/g, ' ');
		vehicleInfo.model = decodeURIComponent(urlModel).replace(/\+/g, ' ');
		return vehicleInfo;
	}
	
	//  Check what's actually selected in vehicle picker dropdowns 
	// First check if we're on a normal search listing page with refine results
	const isNormalSearchListing = document.querySelector('.search-filters-module .vehicle-filter-container, .refine-results, .vehicle-picker, .filter-section');
	
	if (isNormalSearchListing) {
		let yearSelect = null;
		let makeSelect = null;
		let modelSelect = null;

		// Look for selects in refine results area first - specifically target search-filters-module
		const refineResults = document.querySelector('.search-filters-module .vehicle-filter-container, .refine-results, .vehicle-picker, .filter-section');
		if (refineResults) {
			const refineSelects = refineResults.querySelectorAll('select');
			if (refineSelects.length >= 3) {
				// Assume first is make, second is model, third is year
				makeSelect = refineSelects[0];
				modelSelect = refineSelects[1];
				yearSelect = refineSelects[2];
			} else if (refineSelects.length >= 2) {
				// Assume first is make, second is model
				makeSelect = refineSelects[0];
				modelSelect = refineSelects[1];
			}
		}

		// If not found in refine results, try to find by specific class names
		if (!makeSelect) {
			makeSelect = document.querySelector('select.refine-make-select, select[class*="make-select"]');
		}
		if (!modelSelect) {
			modelSelect = document.querySelector('select.refine-model-select, select[class*="model-select"]');
		}
		if (!yearSelect) {
			yearSelect = document.querySelector('select.refine-year-select, select[class*="year-select"]');
		}

		// Fallback to specific selectors
		if (!makeSelect) {
			makeSelect = document.querySelector('select[name*="make"], select[id*="make"], .vehicle-make select, select[data-name="make"]');
		}
		if (!modelSelect) {
			modelSelect = document.querySelector('select[name*="model"], select[id*="model"], .vehicle-model select, select[data-name="model"]');
		}
		if (!yearSelect) {
			yearSelect = document.querySelector('select[name*="year"], select[id*="year"], .vehicle-year select, select[data-name="year"]');
		}

		// Check if we have at least make and model selects
		if (makeSelect && modelSelect) {
			const selectedMake = makeSelect.value;
			const selectedModel = modelSelect.value;
			const selectedYear = yearSelect ? yearSelect.value : null;

			if (selectedMake && selectedMake !== 'all' && selectedMake !== '' && selectedMake !== 'All Makes') {
				vehicleInfo.make = selectedMake;

				if (selectedYear && selectedYear !== 'all' && selectedYear !== '' && selectedYear !== 'All Years') {
					vehicleInfo.year = selectedYear;
				}

				if (selectedModel && selectedModel !== 'all' && selectedModel !== '' && selectedModel !== 'All Models') {
					vehicleInfo.model = selectedModel;
				}

				return vehicleInfo;
			}
		}
	} // End of normal search listing page detection

	// Fallback: Check for any select elements in refine results area - specifically target search-filters-module
	const refineSelects = document.querySelectorAll('.search-filters-module .vehicle-filter-container select, .refine-results select, .vehicle-picker select, select.refine-make-select, select.refine-model-select, select.refine-year-select');
	if (refineSelects.length >= 2) {
		// Try to find the specific selects by class
		let makeSelectFallback = document.querySelector('select.refine-make-select');
		let modelSelectFallback = document.querySelector('select.refine-model-select');
		let yearSelectFallback = document.querySelector('select.refine-year-select');

		// If not found by class, use the first few selects
		if (!makeSelectFallback && refineSelects.length >= 1) {
			makeSelectFallback = refineSelects[0];
		}
		if (!modelSelectFallback && refineSelects.length >= 2) {
			modelSelectFallback = refineSelects[1];
		}
		if (!yearSelectFallback && refineSelects.length >= 3) {
			yearSelectFallback = refineSelects[2];
		}

		const selectedMakeFallback = makeSelectFallback ? makeSelectFallback.value : '';
		const selectedModelFallback = modelSelectFallback ? modelSelectFallback.value : '';
		if (selectedMakeFallback && selectedMakeFallback !== 'all' && selectedMakeFallback !== '' && selectedMakeFallback !== 'All Makes') {
			vehicleInfo.make = selectedMakeFallback;

			if (selectedModelFallback && selectedModelFallback !== 'all' && selectedModelFallback !== '' && selectedModelFallback !== 'All Models') {
				vehicleInfo.model = selectedModelFallback;
			}

			// Check for year if available
			if (yearSelectFallback) {
				const selectedYearFallback = yearSelectFallback.value;
				if (selectedYearFallback && selectedYearFallback !== 'all' && selectedYearFallback !== '' && selectedYearFallback !== 'All Years') {
					vehicleInfo.year = selectedYearFallback;
				}
			}

			return vehicleInfo;
		}
	}

	// Check vehicle picker data attributes (most reliable for DSEO pages)
	const vpGarage = document.querySelector('.vp-category-garage');
	if (vpGarage) {
		const year = vpGarage.getAttribute('data-vehicle-year');
		const make = vpGarage.getAttribute('data-vehicle-make');
		const model = vpGarage.getAttribute('data-vehicle-model');

		if (year && make && model) {
			vehicleInfo.year = year;
			vehicleInfo.make = decodeURIComponent(make).replace(/\+/g, ' ');
			vehicleInfo.model = decodeURIComponent(model).replace(/\+/g, ' ');
			return vehicleInfo;
		}
	}

	// Check vehicle picker wrapper
	const vpWrapper = document.querySelector('.vehiclepicker_wrapper');
	if (vpWrapper) {
		const year = vpWrapper.getAttribute('data-year');
		const make = vpWrapper.getAttribute('data-make');
		const model = vpWrapper.getAttribute('data-model');

		if (year && make && model) {
			vehicleInfo.year = year;
			vehicleInfo.make = decodeURIComponent(make).replace(/\+/g, ' ');
			vehicleInfo.model = decodeURIComponent(model).replace(/\+/g, ' ');
			return vehicleInfo;
		}
	}

	// Check vehicle dropdowns (FIXED - now works with partial selections)
	// First try to find dropdowns in the specific search-filters-module container
	const searchFiltersModule = document.querySelector('.search-filters-module .vehicle-filter-container');
	let yearSelectDropdown = null;
	let makeSelectDropdown = null;
	let modelSelectDropdown = null;

	if (searchFiltersModule) {
		const moduleSelects = searchFiltersModule.querySelectorAll('select');
		if (moduleSelects.length >= 3) {
			makeSelectDropdown = moduleSelects[0];
			modelSelectDropdown = moduleSelects[1];
			yearSelectDropdown = moduleSelects[2];
		} else if (moduleSelects.length >= 2) {
			makeSelectDropdown = moduleSelects[0];
			modelSelectDropdown = moduleSelects[1];
		}
	}

	// Try to find by specific class names first
	if (!makeSelectDropdown) {
		makeSelectDropdown = document.querySelector('select.refine-make-select, select[class*="make-select"]');
	}
	if (!modelSelectDropdown) {
		modelSelectDropdown = document.querySelector('select.refine-model-select, select[class*="model-select"]');
	}
	if (!yearSelectDropdown) {
		yearSelectDropdown = document.querySelector('select.refine-year-select, select[class*="year-select"]');
	}

	// Fallback to generic selectors if not found in specific container
	if (!makeSelectDropdown) {
		makeSelectDropdown = document.querySelector('select[name*="make"], select[id*="make"], .vehicle-make select');
	}
	if (!modelSelectDropdown) {
		modelSelectDropdown = document.querySelector('select[name*="model"], select[id*="model"], .vehicle-model select');
	}
	if (!yearSelectDropdown) {
		yearSelectDropdown = document.querySelector('select[name*="year"], select[id*="year"], .vehicle-year select');
	}
	// Check if we have at least a make dropdown
	if (makeSelectDropdown) {
		const makeValue = makeSelectDropdown.value;
		const makeOption = makeSelectDropdown.querySelector('option:checked');

		// Check if make is actually selected (not default placeholder)
		const isMakeSelected = makeValue && makeOption &&
			!['Make', 'Select Make', 'All Makes', 'all', ''].includes(makeOption.textContent.trim()) &&
			!['Make', 'Select Make', 'All Makes', 'all', ''].includes(makeValue);

		if (isMakeSelected) {
			vehicleInfo.make = decodeURIComponent(makeValue).replace(/\+/g, ' ');
			if (yearSelectDropdown) {
				const yearValue = yearSelectDropdown.value;
				const yearOption = yearSelectDropdown.querySelector('option:checked');
				const isYearSelected = yearValue && yearOption &&
					!['Year', 'Select Year', 'All Years', 'all', ''].includes(yearOption.textContent.trim()) &&
					!['Year', 'Select Year', 'All Years', 'all', ''].includes(yearValue);

				if (isYearSelected) {
					vehicleInfo.year = yearValue;
				}
			}
			// Check for model if available
			if (modelSelectDropdown) {
				const modelValue = modelSelectDropdown.value;
				const modelOption = modelSelectDropdown.querySelector('option:checked');
				const isModelSelected = modelValue && modelOption &&
					!['Model', 'Select Model', 'All Models', 'all', ''].includes(modelOption.textContent.trim()) &&
					!['Model', 'Select Model', 'All Models', 'all', ''].includes(modelValue);

				if (isModelSelected) {
					vehicleInfo.model = decodeURIComponent(modelValue).replace(/\+/g, ' ');
				}
			}
			return vehicleInfo;
		}
	}

	//Check search bar for vehicle text (only if it's not the default placeholder)
	const searchBar = document.querySelector('.search-bar-component input[type="text"]');
	if (searchBar && searchBar.value && searchBar.value !== 'Search All Vehicles') {
		const searchText = searchBar.value;
		const vehicleMatch = searchText.match(/(\d{4})\s+([^,\s]+)\s+([^,\s]+(?:-[^,\s]+)*)/);
		if (vehicleMatch) {
			vehicleInfo.year = vehicleMatch[1];
			vehicleInfo.make = vehicleMatch[2];
			vehicleInfo.model = vehicleMatch[3];
			return vehicleInfo;
		}
	}


	//Check for DSEO page specific elements
	const dseoElements = document.querySelectorAll('[data-vehicle-year], [data-vehicle-make], [data-vehicle-model], [data-year], [data-make], [data-model]');
	if (dseoElements.length > 0) {
		for (let element of dseoElements) {
			const year = element.getAttribute('data-vehicle-year') || element.getAttribute('data-year');
			const make = element.getAttribute('data-vehicle-make') || element.getAttribute('data-make');
			const model = element.getAttribute('data-vehicle-model') || element.getAttribute('data-model');

			if (year && make && model) {
				vehicleInfo.year = year;
				vehicleInfo.make = decodeURIComponent(make).replace(/\+/g, ' ');
				vehicleInfo.model = decodeURIComponent(model).replace(/\+/g, ' ');
				return vehicleInfo;
			}
		}
	}

	//  Check vehicle info based on page type
	// DSEO page: Look for current-vehicle with h4 element
	const currentVehicle = document.querySelector('.current-vehicle, [class*="current-vehicle"]');
	if (currentVehicle) {
		const vehicleH4 = currentVehicle.querySelector('h4');
		if (vehicleH4) {
			const vehicleText = vehicleH4.textContent || vehicleH4.innerText;
			if (vehicleText && vehicleText.match(/\d{4}\s+[A-Za-z\s]+\s+[A-Za-z0-9\s-]+/)) {
				const vehicleMatch = vehicleText.match(/(\d{4})\s+([A-Za-z\s]+)\s+([A-Za-z0-9\s-]+)/);
				if (vehicleMatch && vehicleMatch[1] && vehicleMatch[2] && vehicleMatch[3]) {
					vehicleInfo.year = vehicleMatch[1];
					vehicleInfo.make = vehicleMatch[2].trim();
					vehicleInfo.model = vehicleMatch[3].trim();
					return vehicleInfo;
				}
			}
		}
	}

	// Vehicle selection page: Look for selected-vehicle-tile with p element
	const selectedVehicleTile = document.querySelector('.selected-vehicle-tile, .vehicle-tile, .selected-vehicle, [class*="selected-vehicle"], [class*="vehicle-tile"]');
	if (selectedVehicleTile) {
		const vehicleP = selectedVehicleTile.querySelector('p');
		if (vehicleP) {
			const vehicleText = vehicleP.textContent || vehicleP.innerText;
			if (vehicleText && vehicleText.match(/\d{4}\s+[A-Za-z\s]+\s+[A-Za-z0-9\s-]+/)) {
				const vehicleMatch = vehicleText.match(/(\d{4})\s+([A-Za-z\s]+)\s+([A-Za-z0-9\s-]+)/);
				if (vehicleMatch && vehicleMatch[1] && vehicleMatch[2] && vehicleMatch[3]) {
					vehicleInfo.year = vehicleMatch[1];
					vehicleInfo.make = vehicleMatch[2].trim();
					vehicleInfo.model = vehicleMatch[3].trim();
					return vehicleInfo;
				}
			}
		}
	}

	// Look for vehicle info in main search bar input field
	const searchBarInput = document.querySelector('.search-bar-component input[type="text"], .search-input, input[placeholder*="search"], input[name*="search"], input[id*="search"]');
	if (searchBarInput) {
		const searchValue = searchBarInput.value;
		if (searchValue && searchValue.match(/\d{4}\s+[A-Za-z\s]+\s+[A-Za-z0-9\s-]+/)) {
			const vehicleMatch = searchValue.match(/(\d{4})\s+([A-Za-z\s]+)\s+([A-Za-z0-9\s-]+)/);
			if (vehicleMatch && vehicleMatch[1] && vehicleMatch[2] && vehicleMatch[3]) {
				vehicleInfo.year = vehicleMatch[1];
				vehicleInfo.make = vehicleMatch[2].trim();
				vehicleInfo.model = vehicleMatch[3].trim();
				return vehicleInfo;
			}
		}
	}

	// Look for vehicle info in search bar dropdown
	const searchBarDropdown = document.querySelector('.search-bar-component select, .vehicle-dropdown select, select[class*="vehicle"]');
	if (searchBarDropdown) {
		const searchBarValue = searchBarDropdown.value;
		if (searchBarValue && searchBarValue.match(/\d{4}\s+[A-Za-z\s]+\s+[A-Za-z0-9\s-]+/)) {
			const vehicleMatch = searchBarValue.match(/(\d{4})\s+([A-Za-z\s]+)\s+([A-Za-z0-9\s-]+)/);
			if (vehicleMatch && vehicleMatch[1] && vehicleMatch[2] && vehicleMatch[3]) {
				vehicleInfo.year = vehicleMatch[1];
				vehicleInfo.make = vehicleMatch[2].trim();
				vehicleInfo.model = vehicleMatch[3].trim();
				return vehicleInfo;
			}
		}
	}

	// Look for vehicle info in breadcrumbs
	const breadcrumbElements = document.querySelectorAll('.breadcrumb, .breadcrumbs, [class*="breadcrumb"]');
	for (let element of breadcrumbElements) {
		const text = element.textContent || element.innerText;
		if (text && text.match(/\d{4}\s+[A-Za-z\s]+\s+[A-Za-z0-9\s-]+/)) {
			const vehicleMatch = text.match(/(\d{4})\s+([A-Za-z\s]+)\s+([A-Za-z0-9\s-]+)/);
			if (vehicleMatch && vehicleMatch[1] && vehicleMatch[2] && vehicleMatch[3]) {
				vehicleInfo.year = vehicleMatch[1];
				vehicleInfo.make = vehicleMatch[2].trim();
				vehicleInfo.model = vehicleMatch[3].trim();
				return vehicleInfo;
			}
		}
	}

	const headingElements = document.querySelectorAll('h1, h2, h3, .page-title, .results-title');
	for (let element of headingElements) {
		const text = element.textContent || element.innerText;
		if (text && text.match(/\d{4}\s+[A-Za-z\s]+\s+[A-Za-z0-9\s-]+/)) {
			const vehicleMatch = text.match(/(\d{4})\s+([A-Za-z\s]+)\s+([A-Za-z0-9\s-]+)/);
			if (vehicleMatch && vehicleMatch[1] && vehicleMatch[2] && vehicleMatch[3]) {
				vehicleInfo.year = vehicleMatch[1];
				vehicleInfo.make = vehicleMatch[2].trim();
				vehicleInfo.model = vehicleMatch[3].trim();
				return vehicleInfo;
			}
		}
	}

	// If no vehicle found, return empty object
	return vehicleInfo;
};

searchFacets.getSearchString = function () {
	// Try to get search string from search facets module
	const facetsModule = searchFacets.getFacetsModule();
	if (facetsModule && facetsModule.length) {
		const searchStr = facetsModule.attr('data-search-str');
		if (searchStr) {
			return decodeURIComponent(searchStr);
		}
	}

	// If not found, try to get from URL parameters
	const urlParams = new URLSearchParams(window.location.search);
	return urlParams.get('search_str') || '';
};

searchFacets.getSelectedCategoriesCount = function () {
	const { facetValues } = searchFacets.getFacetValuesFromDom(false);

	// Count category facets (including autocareCategories and other category types)
	const categoryFacetTypes = ['category', 'categories', 'part_category', 'accessory_category', 'autocarecategories'];
	let uniqueCategories = new Set();

	Object.keys(facetValues).forEach(facetName => {
		if (categoryFacetTypes.includes(facetName.toLowerCase())) {
			const facetValuesList = facetValues[facetName];
			if (facetValuesList && Array.isArray(facetValuesList)) {
				facetValuesList.forEach(value => {
					// First replace + with spaces, then decode URI components
					let decodedValue = value.replace(/\+/g, ' ');
					// decodeURIComponent is safe - it only throws on malformed sequences
					decodedValue = decodeURIComponent(decodedValue);

					let categoryName = decodedValue;
					if (decodedValue.includes('>')) {
						categoryName = decodedValue.split('>')[0].trim();
					}

					uniqueCategories.add(categoryName);
				});
			}
		}
	});

	return uniqueCategories.size;
};

searchFacets.getSelectedCategoryName = function () {
	const { facetValues } = searchFacets.getFacetValuesFromDom(false);

	// Get the first selected category name
	const categoryFacetTypes = ['category', 'categories', 'part_category', 'accessory_category', 'autocarecategories'];

	for (const facetName of Object.keys(facetValues)) {
		if (categoryFacetTypes.includes(facetName.toLowerCase())) {
			const facetValuesList = facetValues[facetName];
			if (facetValuesList && Array.isArray(facetValuesList) && facetValuesList.length > 0) {
				let categoryName = facetValuesList[0];
				// First replace + with spaces, then decode URI components
				categoryName = categoryName.replace(/\+/g, ' ');
				// decodeURIComponent is safe - it only throws on malformed sequences
				categoryName = decodeURIComponent(categoryName);

				if (categoryName.includes('>')) {
					categoryName = categoryName.split('>')[0].trim();
				}

				return categoryName;
			}
		}
	}

	return '';
};

searchFacets.updateAppliedFiltersPills = function () {
	const { facetValues } = searchFacets.getFacetValuesFromDom(false, true);

	// Get applied filters containers from the search listing module
	const searchListingModule = document.querySelector('.search-listing-module');
	if (!searchListingModule) {
		return;
	}

	const mobileContainer = searchListingModule.querySelector('#applied-filters-pills-mobile');
	const desktopContainer = searchListingModule.querySelector('#applied-filters-pills-desktop');

	// Clear both containers - let responsive CSS control visibility
	if (mobileContainer) {
		mobileContainer.innerHTML = '';
	}
	if (desktopContainer) {
		desktopContainer.innerHTML = '';
	}

	Object.keys(facetValues).forEach(facetName => {
		const facetValuesList = facetValues[facetName];
		if (facetValuesList && Array.isArray(facetValuesList)) {
			facetValuesList.forEach(value => {
				if (value) {
					const pill = searchFacets.createFilterPill(facetName, value);
					if (mobileContainer) {
						// Create separate pill for mobile to ensure event handlers work
						const mobilePill = searchFacets.createFilterPill(facetName, value);
						mobileContainer.appendChild(mobilePill);
					}
					if (desktopContainer) desktopContainer.appendChild(pill);
				}
			});
		}
	});

	const hasAppliedFilters = Object.keys(facetValues).length > 0;
	const container = searchListingModule.querySelector('.applied-filters-container');
	if (container) {
		if (hasAppliedFilters) {
			container.classList.remove('empty');
		} else {
			container.classList.add('empty');
		}
	}

	if (mobileContainer) {
		mobileContainer.scrollLeft = 0;
	}
	if (desktopContainer) {
		desktopContainer.scrollLeft = 0;
	}

	// Update button states after DOM has rendered
	requestAnimationFrame(() => {
		if (mobileContainer) {
			searchFacets.updatePillsScrollButtons(mobileContainer, 'mobile');
		}
		if (desktopContainer) {
			searchFacets.updatePillsScrollButtons(desktopContainer, 'desktop');
		}
	});
};

searchFacets.createFilterPill = function (facetName, value) {
	const pill = document.createElement('div');
	pill.className = `filter-pill filter-pill-${facetName.toLowerCase().replace(/\s+/g, '-')}`;

	// const checkmark = document.createElement('span');
	const label = document.createElement('span');
	label.className = 'filter-pill-label';

	let displayValue = value;
	try {
		displayValue = decodeURIComponent(displayValue).replace(/\+/g, ' ');
	} catch (e) {
		// If decoding fails, just replace + with spaces
		displayValue = displayValue.replace(/\+/g, ' ');
	}

	// Split category and subcategorites to display as Category: subcategory > subcategory2 > ...
	const normalizedDisplayValue = displayValue.replace(/%3E/gi, '>');
	const parts = normalizedDisplayValue.split('>').map(p => p.trim()).filter(Boolean);
	const hasSubcategories = parts.length > 1;

	const category = document.createElement(hasSubcategories ? 'span' : 'strong');
	const categoryText = (parts[0] || displayValue);
	category.textContent = categoryText + (hasSubcategories ? ': ' : '');
	label.appendChild(category);

	if (hasSubcategories) {
		const subcategoriesContainer = document.createElement('strong');
		const subcategories = parts.slice(1).join(' > ');
		subcategoriesContainer.textContent = subcategories;
		label.appendChild(subcategoriesContainer);	
	}

	const removeBtn = document.createElement('button');
	removeBtn.className = 'filter-pill-remove';
	removeBtn.setAttribute('data-facet-name', facetName);
	removeBtn.setAttribute('data-facet-value', value);
	removeBtn.addEventListener('click', (e) => {
		e.preventDefault();
		e.stopPropagation();
		searchFacets.removeFilterPill(facetName, value);
	});

	// pill.appendChild(checkmark);
	pill.appendChild(label);
	pill.appendChild(removeBtn);

	return pill;
};

searchFacets.removeFilterPill = function (facetName, value) {
	const checkboxes = document.querySelectorAll(`[data-query-name="${facetName}"]`);

	let foundCheckbox = null;

	checkboxes.forEach(checkbox => {
		const checkboxName = checkbox.getAttribute('name');
		const urlName = checkbox.getAttribute('data-url-name');
		const valueToCheck = urlName || checkboxName;

		if (valueToCheck === value) {
			checkbox.checked = false;
			foundCheckbox = checkbox;
		}
	});

	if (foundCheckbox) {
		// Use the checkbox's actual ID and query name for proper handling
		const checkboxId = foundCheckbox.getAttribute('id');
		const queryName = foundCheckbox.getAttribute('data-query-name');
		searchFacets.handleFacetChange(false, checkboxId, queryName, false);
	}
};

/**
 * Redirect to the products page (base URL without facet parameters)
 */
searchFacets.redirectToProductsPage = function() {
	// Get current URL and remove facet parameters
	const currentUrl = new URL(window.location.href);
	const basePath = currentUrl.pathname;
	
	// Remove facet-related query parameters
	const facetParams = ['autocareCategories', 'category', 'page'];
	facetParams.forEach(param => {
		currentUrl.searchParams.delete(param);
	});
	
	// Build the products URL
	const productsUrl = basePath.replace(/\/products.*$/, '/products');
	
	// Redirect to products page
	window.location.href = productsUrl;
};

searchFacets.saveExpandedState = function(facetQueryName, isExpanded) {
	try {
		const expandedStates = JSON.parse(sessionStorage.getItem('facetExpandedStates') || '{}');
		expandedStates[facetQueryName] = isExpanded;
		sessionStorage.setItem('facetExpandedStates', JSON.stringify(expandedStates));
	} catch (e) {
		// Silently handle storage errors
	}
};

searchFacets.getExpandedStates = function() {
	try {
		return JSON.parse(sessionStorage.getItem('facetExpandedStates') || '{}');
	} catch (e) {
		return {};
	}
};

searchFacets.restoreExpandedStates = function() {
	const expandedStates = searchFacets.getExpandedStates();

	Object.keys(expandedStates).forEach((facetQueryName) => {
		const isExpanded = expandedStates[facetQueryName];

		if (isExpanded) {
			document.querySelectorAll('.hidden-filter-' + facetQueryName).forEach(function(el) {
				el.removeAttribute('hidden');
			});

			document.querySelectorAll(`#see-less-mobile-${facetQueryName}, #see-less-desktop-${facetQueryName}`).forEach(function(el) {
				el.removeAttribute('hidden');
				const parentLi = el.closest('li');
				if (parentLi) {
					parentLi.removeAttribute('hidden');
				}
			});
			document.querySelectorAll(`#see-more-mobile-${facetQueryName}, #see-more-desktop-${facetQueryName}`).forEach(function(el) {
				el.setAttribute('hidden', true);
				const parentLi = el.closest('li');
				if (parentLi) {
					parentLi.setAttribute('hidden', true);
				}
			});
		}
	});
};

searchFacets.saveExpandedState = function(facetQueryName, isExpanded) {
	try {
		const expandedStates = JSON.parse(sessionStorage.getItem('facetExpandedStates') || '{}');
		expandedStates[facetQueryName] = isExpanded;
		sessionStorage.setItem('facetExpandedStates', JSON.stringify(expandedStates));
	} catch (e) {
		// Silently handle storage errors
	}
};

searchFacets.getExpandedStates = function() {
	try {
		return JSON.parse(sessionStorage.getItem('facetExpandedStates') || '{}');
	} catch (e) {
		return {};
	}
};

searchFacets.restoreExpandedStates = function() {
	const expandedStates = searchFacets.getExpandedStates();

	Object.keys(expandedStates).forEach((facetQueryName) => {
		const isExpanded = expandedStates[facetQueryName];

		if (isExpanded) {
			document.querySelectorAll('.hidden-filter-' + facetQueryName).forEach(function(el) {
				el.removeAttribute('hidden');
			});

			document.querySelectorAll(`#see-less-mobile-${facetQueryName}, #see-less-desktop-${facetQueryName}`).forEach(function(el) {
				el.removeAttribute('hidden');
				const parentLi = el.closest('li');
				if (parentLi) {
					parentLi.removeAttribute('hidden');
				}
			});
			document.querySelectorAll(`#see-more-mobile-${facetQueryName}, #see-more-desktop-${facetQueryName}`).forEach(function(el) {
				el.setAttribute('hidden', true);
				const parentLi = el.closest('li');
				if (parentLi) {
					parentLi.setAttribute('hidden', true);
				}
			});
		}
	});
};

module.exports = searchFacets;
